<template>
    <v-menu open-on-hover offset-x @input="onMenuToggle">
        <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-inline-block">
                <slot></slot>
            </div>
        </template>
        <v-card v-if="switchSelection" width="400px">
            <v-container fluid>
                <v-row dense>
                    <v-col cols="6" v-if="switchSelection.cpu">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="purple">mdi-memory</v-icon>
                                Processor
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ switchSelection.cpu }}</v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6" v-if="switchSelection.memory">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="purple">mdi-memory</v-icon>
                                RAM
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ toLocaleFixed(switchSelection.memory, 0) }} GB</v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6" v-if="switchSelection.gpu_type">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="light-green">mdi-memory mdi-rotate-45</v-icon>
                                Accelerator
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ switchSelection.gpu_type }}</v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6" v-if="switchSelection.vram">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="light-green">mdi-memory mdi-rotate-45</v-icon>
                                HBM
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ toLocaleFixed(switchSelection.vram, 0) }} GB</v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6" v-if="switchSelection.ssd">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="orange">mdi-nas</v-icon>
                                Temp storage
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ toLocaleFixed(switchSelection.ssd, 0) }} GB</v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-card>
        <v-card v-else-if="app.active_resource === 'ncu'" width="400px">
            <v-container fluid>
                <v-row dense>
                    <v-col cols="6">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="purple">mdi-memory</v-icon>
                                Processor
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ currentNCU(app.ncu).cpu }}</v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="purple">mdi-memory</v-icon>
                                RAM
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ toLocaleFixed(currentNCU(app.ncu).memory, 0) }} GB</v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-card>
        <v-card v-else-if="currentNodePool(app.node_pool)" width="400px">
            <v-container fluid>
                <v-row dense>
                    <v-col cols="6" v-if="currentNodePool(app.node_pool).cpu">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="purple">mdi-memory</v-icon>
                                Processor
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">
                                <v-progress-circular v-if="loadingLimit" indeterminate size="20" width="2" color="primary"></v-progress-circular>
                                <template v-else>{{ allocate(currentNodePool(app.node_pool).cpu) }}</template>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6" v-if="currentNodePool(app.node_pool).memory">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="purple">mdi-memory</v-icon>
                                RAM
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">
                                <v-progress-circular v-if="loadingLimit" indeterminate size="20" width="2" color="primary"></v-progress-circular>
                                <template v-else>{{ toLocaleFixed(allocate(currentNodePool(app.node_pool).memory), 0) }} GB</template>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6" v-if="currentNodePool(app.node_pool).gpu_type">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="light-green">mdi-memory mdi-rotate-45</v-icon>
                                Accelerator
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ currentNodePool(app.node_pool).gpu_type }}</v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6" v-if="currentNodePool(app.node_pool).vram">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="light-green">mdi-memory mdi-rotate-45</v-icon>
                                HBM
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">
                                <v-progress-circular v-if="loadingLimit" indeterminate size="20" width="2" color="primary"></v-progress-circular>
                                <template v-else>{{ toLocaleFixed(allocate(currentNodePool(app.node_pool).vram), 0) }} GB</template>
                            </v-card-text>
                        </v-card>
                    </v-col>
                    <v-col cols="6" v-if="currentNodePool(app.node_pool).ssd">
                        <v-card flat class="grey lighten-3 fill-height">
                            <v-card-title class="caption pb-1">
                                <v-icon small left color="orange">mdi-nas</v-icon>
                                Temp storage
                            </v-card-title>
                            <v-card-text class="text-h6 text-break">{{ toLocaleFixed(currentNodePool(app.node_pool).ssd, 0) }} GB</v-card-text>
                        </v-card>
                    </v-col>
                </v-row>
            </v-container>
        </v-card>
    </v-menu>
</template>

<script>
import { scaling } from '@/mixins/scaling'

export default {
    name: 'AppScaleInfoMenu',
    components: {},
    mixins: [scaling],
    props: {
        app: Object
    },
    data() {
        return {
            resourceLimit: 100,
            loadingLimit: false
        }
    },
    created() {},
    beforeDestroy() {},
    methods: {
        allocate(total) {
            if (!total) return 0
            if (!this.isFractional) return total
            return Math.round(((total * this.resourceLimit) / 100) * 10) / 10
        },
        onMenuToggle(open) {
            if (!open || !this.isFractional) return
            this.fetchResourceLimit()
        },
        fetchResourceLimit() {
            if (!this.app || !this.app.aid) return
            this.loadingLimit = true
            this.$axios
                .get(`/applications/${this.app.aid}/config`)
                .then(({ data }) => {
                    this.resourceLimit = data.hami_resource_limit || 100
                })
                .catch(err => {
                    console.log(err)
                })
                .finally(() => {
                    this.loadingLimit = false
                })
        }
    },
    computed: {
        isFractional() {
            return Boolean(this.app && this.app.node_pool && this.app.node_pool.includes('-hami'))
        },
        switchSelection() {
            if (!this.isSwitchCloudEnabled()) return null
            if (!this.app || this.app.active_resource === 'ncu') return null
            const nodepool = this.currentLocalNodePool(this.app.node_pool)
            if (!nodepool || nodepool.cloud !== 'switch') return null
            return nodepool
        }
    },
    watch: {}
}
</script>
